import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { ManagerSelect } from './TeamDashboard';
import '../../styles/Routines.css';
import '../../styles/TeamDashboard.css';

const PERIODICITIES = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
const PERIODICITY_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

function buildMonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    opts.push({ value: val, label });
  }
  return opts;
}

function Avatar({ name, photoUrl }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('');
  if (photoUrl) {
    return (
      <div className="rt-avatar" style={{ padding: 0, overflow: 'hidden' }}>
        <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
      </div>
    );
  }
  return <div className="rt-avatar">{initials || '?'}</div>;
}

function OdometerTile({ type, pct: rawPct }) {
  const pct   = Math.min(100, Math.max(0, rawPct ?? 0));
  const label = PERIODICITY_LABEL[type] || type;
  const cls   = pct >= 80 ? 'high' : pct >= 50 ? 'mid' : 'low';
  const circ  = 2 * Math.PI * 28;
  const dash  = circ * (1 - pct / 100);

  return (
    <div className="rt-odometer-tile">
      <div className={`rt-odometer-ring rt-odometer-${cls}`}>
        <svg viewBox="0 0 64 64" width="72" height="72">
          <circle cx="32" cy="32" r="28" className="rt-odometer-track" />
          <circle
            cx="32" cy="32" r="28"
            className={`rt-odometer-fill rt-odometer-fill-${cls}`}
            strokeDasharray={circ}
            strokeDashoffset={dash}
            transform="rotate(-90 32 32)"
          />
        </svg>
        <span className="rt-odometer-pct">{pct}%</span>
      </div>
      <span className="rt-odometer-label">{label}</span>
    </div>
  );
}

function CrossTabCell({ routineStats, periodicity }) {
  const items = routineStats.filter(r => r.routine?.periodicity === periodicity);
  if (items.length === 0) return <span className="rt-no-data">—</span>;

  const shown = items.slice(0, 3);
  const extra = items.length - 3;

  return (
    <div className="rt-type-cell">
      {shown.map(item => {
        const done = item.tasks_completed ?? 0;
        const exp  = item.expected_count ?? 0;
        const cls  = exp > 0 && done >= exp ? 'rt-tasks-done' : done > 0 ? 'rt-tasks-partial' : '';
        return (
          <div key={item.routine.id} className="rt-type-cell-row">
            <span className="rt-type-cell-name" title={item.routine.description}>
              {item.routine.description}
            </span>
            {exp > 0 && (
              <span className={`rt-type-cell-count ${cls}`}>{done}/{exp}</span>
            )}
          </div>
        );
      })}
      {extra > 0 && <span className="rt-type-cell-more">+{extra} more</span>}
    </div>
  );
}

function deriveStatus(item) {
  if (item.status) return item.status;
  const pct = item.combined_pct;
  if (pct == null) return 'not_started';
  if (pct === 100)  return 'completed';
  if (pct === 0)    return 'incomplete';
  return 'pending';
}

function RoutineStatusBadge({ status }) {
  const map = {
    completed:      { label: 'Completed',   cls: 'completed' },
    pending:        { label: 'Pending',     cls: 'pending' },
    incomplete:     { label: 'Incomplete',  cls: 'incomplete' },
    not_started:    { label: 'Not Started', cls: 'not-started' },
    not_applicable: { label: 'N/A',         cls: 'not-started' },
  };
  const { label, cls } = map[status] || { label: status, cls: 'not-started' };
  return <span className={`rt-status-badge rt-status-${cls}`}>{label}</span>;
}

export function MemberDetailPanel({ person, onBack, appId }) {
  const { user, primary_role_name } = person || {};
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Unknown';

  const api             = useApi();
  const { searchQuery } = useWorkspace();
  const monthOptions    = useMemo(buildMonthOptions, []);

  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const extra = { mode: 'month', month: selectedMonth };
    if (user?.id) extra.userId = user.id;
    api.getRoutineDashboard(appId, undefined, extra)
      .then(setData)
      .catch((err) => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [appId, user?.id, selectedMonth]);

  const routine_stats     = data?.routine_stats || [];
  const overall_efficiency = data?.overall_efficiency ?? person?.overall_efficiency;
  const presentTypes      = PERIODICITIES.filter(p => routine_stats.some(r => r.routine?.periodicity === p));

  // Per-periodicity efficiency for odometer tiles.
  // Each routine's own completion is capped at 100% before being folded into
  // the type average — otherwise an over-achieving routine (e.g. 22/5) can
  // mathematically absorb another routine's shortfall (e.g. 4/5) when only
  // the final sum is clamped, masking genuinely incomplete routines.
  const typeEfficiency = {};
  for (const p of presentTypes) {
    const items = routine_stats.filter(r => r.routine?.periodicity === p);
    const capped = items.map(i => {
      const exp = i.expected_count ?? 0;
      if (exp <= 0) return 0;
      return Math.min(100, ((i.tasks_completed ?? 0) / exp) * 100);
    });
    typeEfficiency[p] = capped.length > 0 ? Math.round(capped.reduce((s, v) => s + v, 0) / capped.length) : 0;
  }

  const sq = (searchQuery || '').toLowerCase().trim();
  const filteredStats = routine_stats.filter(item => {
    if (!sq) return true;
    return [(item.routine?.description || ''), (item.role_name || ''), (item.area_name || '')].some(f => f.toLowerCase().includes(sq));
  });

  // Group routines by name+role+area for the cross-tab rows
  // Each unique routine is one row; columns are periodicity types
  // Since each routine has one periodicity, each row will have a value in only one periodicity column
  const effCls = overall_efficiency == null ? '' : overall_efficiency >= 80 ? 'rt-eff-high' : overall_efficiency >= 50 ? 'rt-eff-mid' : 'rt-eff-low';

  return (
    <div className="rt-detail-panel">
      <div className="rt-detail-topbar">
        {onBack && <button className="btn-back" onClick={onBack}>←</button>}
        <select
          className="rt-filter-select"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        >
          {monthOptions.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading && <div className="rt-loading"><span className="rt-spinner" />Loading…</div>}
      {error && <div className="banner banner-error">{error}</div>}

      {/* Odometer tiles only */}
      <div className="rt-odometer-row" style={{ marginBottom: 20 }}>
        {presentTypes.map(p => (
          <OdometerTile key={p} type={p} pct={typeEfficiency[p] ?? 0} />
        ))}
      </div>

      {routine_stats.length === 0 ? (
        <div className="rt-empty">
          <div className="rt-empty-icon">📋</div>
          <p className="rt-empty-title">No routines this period</p>
          <p>No routines are defined for this member's role(s).</p>
        </div>
      ) : (
        <div className="rt-members-card">
          <div className="rt-breakdown-header">
            <span className="rt-breakdown-title">Routine Summary</span>
            <span className="rt-breakdown-sub">
              {monthOptions.find(o => o.value === selectedMonth)?.label}
            </span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="rt-crosstab-table">
              <thead>
                <tr>
                  <th className="rt-serial-th">Sl No</th>
                  <th>Routine / Activity</th>
                  <th>Role · Area</th>
                  {presentTypes.map(p => (
                    <th key={p} className="rt-type-th">{PERIODICITY_LABEL[p]}</th>
                  ))}
                  <th className="rt-eff-th">Overall %</th>
                </tr>
              </thead>
              <tbody>
                {filteredStats.map((item, idx) => {
                  const done = item.tasks_completed ?? 0;
                  const exp  = item.expected_count ?? 0;
                  const pct  = exp > 0 ? Math.min(100, Math.round((done / exp) * 100)) : null;
                  const pctCls = pct == null ? '' : pct >= 80 ? 'rt-eff-high' : pct >= 50 ? 'rt-eff-mid' : 'rt-eff-low';
                  return (
                    <tr key={item.routine.id} className="rt-member-row">
                      <td className="rt-serial-cell">{idx + 1}</td>
                      <td>
                        <div className="rt-routine-name">{item.routine.description}</div>
                        {item.routine.periodicity && (
                          <div className="rt-routine-period-tag">{item.routine.periodicity}</div>
                        )}
                      </td>
                      <td className="rt-area-cell">
                        <div>{item.role_name || '—'}</div>
                        {item.area_name && <div className="rt-area-sub">{item.area_name}</div>}
                      </td>
                      {presentTypes.map(p => (
                        <td key={p} className="rt-type-col">
                          {item.routine?.periodicity === p ? (
                            exp > 0 ? (
                              <span className={done >= exp ? 'rt-tasks-done' : done > 0 ? 'rt-tasks-partial' : 'rt-no-data'}>
                                {done}/{exp}
                              </span>
                            ) : <span className="rt-no-data">—</span>
                          ) : (
                            <span className="rt-no-data">—</span>
                          )}
                        </td>
                      ))}
                      <td className="rt-eff-cell">
                        {pct != null ? (
                          <span className={`rt-eff-badge ${pctCls}`}>{pct}%</span>
                        ) : (
                          <span className="rt-no-data">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-muted)' }}>
                  <td />
                  <td style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-secondary)', paddingLeft: 10 }}>Overall</td>
                  <td />
                  {presentTypes.map(p => (
                    <td key={p} className="rt-type-col">
                      <span className={typeEfficiency[p] >= 80 ? 'rt-tasks-done' : typeEfficiency[p] > 0 ? 'rt-tasks-partial' : 'rt-no-data'}>
                        {typeEfficiency[p]}%
                      </span>
                    </td>
                  ))}
                  <td className="rt-eff-cell">
                    {overall_efficiency != null ? (
                      <span className={`rt-eff-badge ${effCls}`}>{overall_efficiency}%</span>
                    ) : <span className="rt-no-data">—</span>}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TeamRoutines() {
  const { appId } = useParams();
  const api = useApi();
  const { searchQuery, canWrite, app } = useWorkspace();
  const [searchParams] = useSearchParams();
  const showAdminScope = canWrite || app?.iAmOwner;

  const monthOptions = useMemo(buildMonthOptions, []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [data, setData]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // scope: 'direct' | 'all' | 'admin' | null (null = not yet determined)
  const [scope, setScope] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState(
    searchParams.get('view_as') ? Number(searchParams.get('view_as')) : null
  );
  const [hasDirectReportees,   setHasDirectReportees]   = useState(false);
  const [hasIndirectReportees, setHasIndirectReportees] = useState(false);
  const [subManagers, setSubManagers] = useState([]);

  // Reset sub-manager filter when scope changes
  useEffect(() => {
    setSelectedManagerId(null);
  }, [scope]);

  // On mount, determine scope and capabilities from a single 'direct' probe call,
  // same pattern as the Manager Dashboard, so this page's selector always has the
  // same options and the same default as arriving from there.
  useEffect(() => {
    const urlScope = searchParams.get('scope');
    api.getManagerDashboard(appId, 'direct')
      .then(d => {
        const hasDirect   = d.has_direct_reportees   ?? !!(d.members && d.members.length > 0);
        const hasIndirect = d.has_indirect_reportees ?? false;
        setHasDirectReportees(hasDirect);
        setHasIndirectReportees(hasIndirect);
        setSubManagers(d.sub_managers || []);
        if (urlScope) {
          setScope(urlScope);
        } else if (showAdminScope) {
          setScope('admin');
        } else {
          setScope('direct');
        }
      })
      .catch(() => setScope(urlScope || (showAdminScope ? 'admin' : 'direct')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  useEffect(() => {
    if (!scope) return;
    setLoading(true);
    setError(null);
    setSelectedMember(null);
    const extra = { mode: 'month', month: selectedMonth, scope };
    api.getTeamRoutineDashboard(appId, undefined, extra, scope === 'all' ? selectedManagerId : null)
      .then(setData)
      .catch((err) => setError(err.response?.data?.error || 'Failed to load team routines'))
      .finally(() => setLoading(false));
  }, [appId, selectedMonth, scope, selectedManagerId]);

  if (selectedMember) {
    return (
      <div className="setup-page">
        <h1 className="page-title">Team Routines</h1>
        <p className="page-subtitle">
          Track your team's recurring role-based activities — daily, weekly, monthly, quarterly, or yearly —
          and see how consistently each member is keeping up with them.
        </p>
        <MemberDetailPanel
          person={selectedMember}
          appId={appId}
          onBack={() => setSelectedMember(null)}
        />
      </div>
    );
  }

  const reportees = data?.reportees || [];

  // Determine which periodicity types have any routines across the team
  const allStats     = reportees.flatMap(r => r.routine_stats || []);
  const activeTypes  = PERIODICITIES.filter(p => allStats.some(r => r.routine?.periodicity === p));
  const type_stats   = data?.type_stats || {};

  const filteredReportees = reportees.filter(person => {
    const sq = (searchQuery || '').toLowerCase().trim();
    if (!sq) return true;
    const n = ([person.user?.first_name, person.user?.last_name].filter(Boolean).join(' ') || person.user?.email || '').toLowerCase();
    return n.includes(sq) || (person.primary_role_name || '').toLowerCase().includes(sq);
  });

  return (
    <div className="setup-page">
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title">Team Routines</h1>
        <p className="page-subtitle">
          Track your team's recurring role-based activities — daily, weekly, monthly, quarterly, or yearly —
          and see how consistently each member is keeping up with them.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          <select
            value={scope || ''}
            onChange={e => setScope(e.target.value)}
            className="tdb-scope-select"
          >
            {hasDirectReportees && <option value="direct">Direct Reportees</option>}
            {hasIndirectReportees && <option value="all">Entire Team</option>}
            {showAdminScope && <option value="admin">All Members</option>}
          </select>
          {scope === 'all' && subManagers.length > 0 && (
            <ManagerSelect
              value={selectedManagerId ?? ''}
              onChange={v => setSelectedManagerId(v ? Number(v) : null)}
              options={subManagers}
            />
          )}
          {!loading && !error && data && (
            <select
              className="rt-filter-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              {monthOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>
      {loading && (
        <div className="rt-loading">
          <span className="rt-spinner" />
          Loading team routines…
        </div>
      )}
      {error && <div className="banner banner-error" style={{ margin: '16px 0' }}>{error}</div>}

      {!loading && !error && data && (
        <>
          {!data.has_reporting_setup || reportees.length === 0 ? (
            <div className="rt-empty">
              <div className="rt-empty-icon">👥</div>
              <p className="rt-empty-title">No team data available</p>
              <p>No reporting structure is set up or no members found.</p>
            </div>
          ) : (
            <>
              {/* Odometer tiles row — month picker now lives in the header row above */}
              <div className="rt-odometer-topbar">
                <div className="rt-odometer-row">
                  {activeTypes.map(p => (
                    type_stats[p] != null && (
                      <OdometerTile key={p} type={p} pct={type_stats[p]} />
                    )
                  ))}
                </div>
              </div>

              {/* Cross-tab member table */}
              <div className="rt-members-card">
                <div className="rt-breakdown-header">
                  <span className="rt-breakdown-title">Team Routine Summary</span>
                  <span className="rt-breakdown-sub">
                    {monthOptions.find(o => o.value === selectedMonth)?.label} · {reportees.length} member{reportees.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="rt-crosstab-table">
                    <thead>
                      <tr>
                        <th className="rt-serial-th">Sl No</th>
                        <th>Member</th>
                        {activeTypes.map(p => (
                          <th key={p} className="rt-type-th">{PERIODICITY_LABEL[p]}</th>
                        ))}
                        <th className="rt-eff-th">Overall %</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReportees.map((person, idx) => {
                        const name  = [person.user?.first_name, person.user?.last_name].filter(Boolean).join(' ') || person.user?.email || 'Unknown';
                        const eff   = person.overall_efficiency;
                        const effCls = eff === null ? '' : eff >= 80 ? 'rt-eff-high' : eff >= 50 ? 'rt-eff-mid' : 'rt-eff-low';

                        return (
                          <tr
                            key={person.user?.id}
                            className={`rt-member-row${person.is_manager ? ' rt-manager-row' : ''}`}
                          >
                            <td className="rt-serial-cell">{idx + 1}</td>
                            <td className="rt-member-cell">
                              <Avatar name={name} photoUrl={person.user?.photo_url} />
                              <div className="rt-member-info">
                                <span className="rt-member-name-text">
                                  {name}
                                  {person.is_manager && <span className="rt-manager-badge">Manager</span>}
                                </span>
                                {person.primary_role_name && (
                                  <span className="rt-member-role-text">{person.primary_role_name}</span>
                                )}
                              </div>
                            </td>
                            {activeTypes.map(p => (
                              <td key={p} className="rt-type-col">
                                <CrossTabCell routineStats={person.routine_stats || []} periodicity={p} />
                              </td>
                            ))}
                            <td className="rt-eff-cell">
                              {eff !== null && eff !== undefined ? (
                                <span className={`rt-eff-badge ${effCls}`}>{eff}%</span>
                              ) : (
                                <span className="rt-no-data">—</span>
                              )}
                            </td>
                            <td>
                              <button
                                className="rt-view-details-btn"
                                onClick={() => setSelectedMember(person)}
                              >
                                Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
